import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    mobile: '',
    company_name: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.status === 'success') {
        toast.success('Thank you! We will contact you soon.');
        setFormData({
          full_name: '',
          email: '',
          mobile: '',
          company_name: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Banner */}
      <section className="bg-[#0B2C4D] text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Get in touch with our export facilitation experts
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#0B2C4D] mb-8">Get In Touch</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
                  <MapPin className="w-8 h-8 text-[#C9A24D] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-[#0B2C4D] mb-2">Address</h3>
                    <p className="text-gray-700">
                      Shop-62, Indira Nagar, Chembur Colony,<br />
                      Mumbai – 400074, Maharashtra, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
                  <Phone className="w-8 h-8 text-[#C9A24D] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-[#0B2C4D] mb-2">Phone</h3>
                    <p className="text-gray-700">
                      8652348614<br />
                      8928035752<br />
                      7710824151
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
                  <Mail className="w-8 h-8 text-[#C9A24D] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-[#0B2C4D] mb-2">Email</h3>
                    <a 
                      href="mailto:ssm@goldenport.in" 
                      className="text-gray-700 hover:text-[#C9A24D]"
                    >
                      ssm@goldenport.in
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#0B2C4D] text-white p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-[#C9A24D]">Business Hours</h3>
                <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                <p>Saturday: 9:00 AM - 1:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-[#C9A24D]">
              <h2 className="text-3xl font-bold text-[#0B2C4D] mb-6">Send us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#0B2C4D] mb-2">
                    Full Name *
                  </label>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    className="border-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0B2C4D] mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="border-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0B2C4D] mb-2">
                    Mobile *
                  </label>
                  <Input
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXXXXXXX"
                    className="border-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0B2C4D] mb-2">
                    Company Name
                  </label>
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Your company name"
                    className="border-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0B2C4D] mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your export requirements..."
                    rows={5}
                    className="border-gray-300"
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-[#0B2C4D] hover:bg-[#0a2542] text-white border border-[#C9A24D] text-lg"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit Enquiry
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
